/**
 * Leitura da limpeza automática diária das keys (GET /keys-cleanup-auto).
 *
 * Puro, como o keyCleanup: o que a tela precisa responder é "rodou hoje?", e
 * errar isso (mostrar "ok" para uma rodada que não aconteceu) é exatamente o
 * silêncio que a rotina existe para evitar.
 */
import type { AutoCleanupKeyResult, AutoCleanupRun, AutoCleanupState, KeyCleanupSkipped } from '@/types/custom-targeting';
import { parseApiDate } from './apiDate';

/** Passou disso da hora marcada sem rodada do dia: algo travou (container fora, scheduler parado). */
export const AUTO_CLEANUP_LATE_MS = 2 * 60 * 60 * 1000;

export type AutoCleanupTone = 'ok' | 'running' | 'waiting' | 'paused' | 'warning' | 'error';

export interface AutoCleanupSummary {
    tone: AutoCleanupTone;
    title: string;
    /** A rodada de hoje (dia UTC), se existir. */
    today?: AutoCleanupRun;
}

export const RUN_STATUS_LABEL: Record<AutoCleanupRun['status'], string> = {
    running: 'Rodando',
    done: 'Concluída',
    done_with_errors: 'Concluída com falhas',
    failed: 'Falhou',
    interrupted: 'Interrompida',
};

export const KEY_STATUS_LABEL: Record<AutoCleanupKeyResult['status'], string> = {
    limpa: 'Limpa',
    pulada: 'Pulada',
    falhou: 'Falhou',
};

/** O dia da rodada é em UTC, como o backend grava o `_id`. */
export function utcDay(date: Date): string {
    return date.toISOString().slice(0, 10);
}

function hora(date: Date | null): string {
    return date ? date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '—';
}

export function summarize(state: AutoCleanupState, now: Date, plural: (n: number) => string): AutoCleanupSummary {
    const today = state.runs.find((r) => r.day === utcDay(now));

    if (today) {
        const fim = parseApiDate(today.finished_at);
        switch (today.status) {
            case 'running':
                return { tone: 'running', title: `Rodando agora — ${plural(today.deactivated)} até aqui`, today };
            case 'done':
                return { tone: 'ok', title: `Rodou hoje às ${hora(fim)} — ${plural(today.deactivated)}`, today };
            case 'done_with_errors':
                return { tone: 'warning', title: `Rodou hoje às ${hora(fim)}, com falha em alguma key — ${plural(today.deactivated)}`, today };
            case 'failed':
                return { tone: 'error', title: 'A rodada de hoje falhou', today };
            case 'interrupted':
                return { tone: 'error', title: 'A rodada de hoje foi interrompida', today };
        }
    }

    if (!state.enabled) return { tone: 'paused', title: 'Pausada — não roda até ser religada' };

    const proxima = parseApiDate(state.next_run_at);
    if (proxima && now.getTime() - proxima.getTime() > AUTO_CLEANUP_LATE_MS) {
        return { tone: 'error', title: `Deveria ter rodado hoje às ${hora(proxima)} e não rodou` };
    }
    if (proxima && now < proxima) return { tone: 'waiting', title: `Ainda não rodou hoje — próxima às ${hora(proxima)}` };
    return { tone: 'waiting', title: 'Deve começar nos próximos minutos' };
}

export function keyCounts(run: AutoCleanupRun): Record<AutoCleanupKeyResult['status'], number> {
    const c = { limpa: 0, pulada: 0, falhou: 0 };
    for (const k of run.keys) c[k.status] += 1;
    return c;
}

/** Keys com algo a mostrar primeiro: falhas, depois as limpas; puladas no fim. */
export function orderedKeys(run: AutoCleanupRun): AutoCleanupKeyResult[] {
    const peso = { falhou: 0, limpa: 1, pulada: 2 };
    return [...run.keys].sort((a, b) => peso[a.status] - peso[b.status] || b.deactivated - a.deactivated);
}

/** Duração em minutos, para a linha da rodada. */
export function runMinutes(run: AutoCleanupRun): number | null {
    const ini = parseApiDate(run.started_at);
    const fim = parseApiDate(run.finished_at);
    if (!ini || !fim) return null;
    return Math.max(0, Math.round((fim.getTime() - ini.getTime()) / 60000));
}

export function skippedTotal(s: KeyCleanupSkipped | null | undefined): number {
    if (!s) return 0;
    return (s.receita ?? 0) + (s.trafego ?? 0) + (s.recente ?? 0) + (s.na_fila ?? 0);
}
